import { z } from "zod";

export const StatusSchema = z.enum(["draft", "reviewed", "published", "archived"]);
export const EvidenceLevelSchema = z.enum([
  "project-evidence",
  "external-evidence",
  "expert-curated",
  "provisional",
]);
export const TrendStatusSchema = z.enum(["emerging", "growing", "established", "transforming"]);
export const EntityTypeSchema = z.enum(["technology", "skill", "career", "sector"]);
export const DisciplineIdSchema = z.enum(["biology", "chemistry", "interdisciplinary"]);
export const SkillGroupIdSchema = z.enum([
  "scientific-lab",
  "digital-data",
  "research",
  "green-sustainability",
  "entrepreneurial-innovation",
  "transferable-professional",
]);
export const ResourceTypeSchema = z.enum([
  "virtual-lab",
  "module-topic",
  "mooc",
  "workshop",
  "research-based-activity",
  "problem-based-activity",
  "entrepreneurship-activity",
  "project-deliverable",
]);
export const CareerClusterIdSchema = z.enum([
  "biotechnology",
  "pharmaceutical",
  "analytical-qc",
  "environmental",
  "food-agrobiotech",
  "research-academia",
  "bioinformatics-data",
  "chemical-industry",
  "green-chemistry",
  "scientific-entrepreneurship",
  "exploring",
]);
export const StageIdSchema = z.enum([
  "masters-student",
  "recent-graduate",
  "doctoral-candidate",
  "researcher",
  "academic-staff",
  "other",
]);

export const GovernanceSchema = z.object({
  status: StatusSchema,
  evidenceLevel: EvidenceLevelSchema,
  lastReviewedAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  sourceIds: z.array(z.string().min(1)).min(1),
  reviewStatus: z.enum(["draft", "reviewed", "published", "archived"]),
});

export const SourceSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    organization: z.string().min(1),
    title: z.string().min(1),
    url: z.string().url().nullable(),
    date: z.string().nullable(),
    accessDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    geography: z.array(z.string().min(1)),
    notes: z.string(),
  })
  .merge(GovernanceSchema.pick({ status: true }));

export const RegionSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  aliases: z.array(z.string()).default([]),
});

export const DisciplineSchema = z.object({
  id: DisciplineIdSchema,
  name: z.string().min(1),
  shortDefinition: z.string().min(1),
});

export const DomainSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  shortDefinition: z.string().min(1),
  disciplineIds: z.array(DisciplineIdSchema).min(1),
});

export const SectorSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    shortDefinition: z.string().min(1),
    disciplineIds: z.array(DisciplineIdSchema).min(1),
    aliases: z.array(z.string()).default([]),
    trendStatus: TrendStatusSchema,
    geography: z.array(z.string().min(1)).min(1),
  })
  .merge(GovernanceSchema);

export const SkillGroupSchema = z.object({
  id: SkillGroupIdSchema,
  name: z.string().min(1),
  shortDefinition: z.string().min(1),
});

export const FrameworkMappingSchema = z.object({
  framework: z.enum(["ResearchComp", "DigComp", "EntreComp", "GreenComp"]),
  area: z.string().min(1),
  note: z.string().optional(),
});

export const SkillSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    shortDefinition: z.string().min(1),
    groupId: SkillGroupIdSchema,
    disciplineIds: z.array(DisciplineIdSchema).min(1),
    domainIds: z.array(z.string()).default([]),
    relatedTechnologyIds: z.array(z.string()).default([]),
    relatedCareerIds: z.array(z.string()).default([]),
    relatedResourceIds: z.array(z.string()).default([]),
    frameworkMappings: z.array(FrameworkMappingSchema).default([]),
    aliases: z.array(z.string()).default([]),
    whyItMatters: z.string().min(1),
    escoSkillUri: z.string().url().nullable().optional(),
    trendStatus: TrendStatusSchema,
    geography: z.array(z.string().min(1)).min(1),
  })
  .merge(GovernanceSchema);

export const TechnologySchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    shortDefinition: z.string().min(1),
    whyItMatters: z.string().min(1),
    disciplineIds: z.array(DisciplineIdSchema).min(1),
    domainIds: z.array(z.string()).default([]),
    relatedSkillIds: z.array(z.string()).min(1),
    relatedCareerIds: z.array(z.string()).default([]),
    relatedSectorIds: z.array(z.string()).default([]),
    relatedResourceIds: z.array(z.string()).default([]),
    aliases: z.array(z.string()).default([]),
    trendStatus: TrendStatusSchema,
    geography: z.array(z.string().min(1)).min(1),
    categoryGroupId: SkillGroupIdSchema,
  })
  .merge(GovernanceSchema);

export const CareerSkillRequirementSchema = z.object({
  skillId: z.string().min(1),
  importance: z.number().int().min(1).max(5),
  targetLevel: z.number().int().min(1).max(4),
  rationale: z.string().min(1),
  sourceIds: z.array(z.string().min(1)).min(1),
});

export const CareerSchema = z
  .object({
    id: z.string().min(1),
    title: z.string().min(1),
    slug: z.string().min(1),
    description: z.string().min(1),
    disciplineIds: z.array(DisciplineIdSchema).min(1),
    clusterIds: z.array(CareerClusterIdSchema).min(1),
    sectorIds: z.array(z.string()).min(1),
    requiredSkills: z.array(CareerSkillRequirementSchema).min(8),
    relatedCareerIds: z.array(z.string()).default([]),
    relatedTechnologyIds: z.array(z.string()).default([]),
    aliases: z.array(z.string()).default([]),
    displayOrder: z.number().int(),
    escoOccupationUri: z.string().url().nullable().optional(),
    trendStatus: TrendStatusSchema,
    geography: z.array(z.string().min(1)).min(1),
  })
  .merge(GovernanceSchema);

export const CareerClusterSchema = z.object({
  id: CareerClusterIdSchema,
  name: z.string().min(1),
  shortDefinition: z.string().min(1),
  exampleCareerIds: z.array(z.string()).min(1),
});

export const TrendSchema = z
  .object({
    id: z.string().min(1),
    title: z.string().min(1),
    description: z.string().min(1),
    whyItMatters: z.string().min(1),
    disciplineIds: z.array(DisciplineIdSchema).min(1),
    categoryGroupId: SkillGroupIdSchema,
    trendStatus: TrendStatusSchema,
    geography: z.array(z.string().min(1)).min(1),
    relatedSkillIds: z.array(z.string()).min(1),
    relatedCareerIds: z.array(z.string()).default([]),
    relatedTechnologyIds: z.array(z.string()).default([]),
    relatedSectorIds: z.array(z.string()).default([]),
    referencePeriod: z.string().min(1),
    aliases: z.array(z.string()).default([]),
  })
  .merge(GovernanceSchema);

export const LearningResourceSchema = z
  .object({
    id: z.string().min(1),
    title: z.string().min(1),
    resourceType: ResourceTypeSchema,
    description: z.string().min(1),
    url: z.string().url().nullable(),
    availability: z.enum(["available", "coming-soon"]),
    skillsDeveloped: z.array(z.string()).default([]),
    targetAudience: z.array(z.string()).default([]),
    language: z.string().min(1),
  })
  .merge(GovernanceSchema);

export const AssessmentQuestionSchema = z.object({
  id: z.string().min(1),
  skillId: z.string().min(1),
  prompt: z.string().min(1),
});

export const CatalogMetaSchema = z.object({
  id: z.literal("boostscience-observatory-catalog"),
  version: z.string().min(1),
  lastReviewedAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  contentNotice: z.string().min(1),
});

export const CatalogSchema = z.object({
  meta: CatalogMetaSchema,
  regions: z.array(RegionSchema).min(1),
  disciplines: z.array(DisciplineSchema).min(3),
  domains: z.array(DomainSchema).min(1),
  skillGroups: z.array(SkillGroupSchema).length(6),
  sectors: z.array(SectorSchema).min(1),
  sources: z.array(SourceSchema).min(1),
  skills: z.array(SkillSchema).min(1),
  technologies: z.array(TechnologySchema).min(1),
  careers: z.array(CareerSchema).min(1),
  clusters: z.array(CareerClusterSchema).min(1),
  trends: z.array(TrendSchema).min(1),
  resources: z.array(LearningResourceSchema),
  questions: z.array(AssessmentQuestionSchema),
});

export type Status = z.infer<typeof StatusSchema>;
export type EvidenceLevel = z.infer<typeof EvidenceLevelSchema>;
export type TrendStatus = z.infer<typeof TrendStatusSchema>;
export type EntityType = z.infer<typeof EntityTypeSchema>;
export type DisciplineId = z.infer<typeof DisciplineIdSchema>;
export type SkillGroupId = z.infer<typeof SkillGroupIdSchema>;
export type CareerClusterId = z.infer<typeof CareerClusterIdSchema>;
export type StageId = z.infer<typeof StageIdSchema>;
export type Catalog = z.infer<typeof CatalogSchema>;
export type Skill = z.infer<typeof SkillSchema>;
export type Career = z.infer<typeof CareerSchema>;
export type Technology = z.infer<typeof TechnologySchema>;
export type Trend = z.infer<typeof TrendSchema>;
export type Sector = z.infer<typeof SectorSchema>;
export type Source = z.infer<typeof SourceSchema>;
export type LearningResource = z.infer<typeof LearningResourceSchema>;
export type CareerSkillRequirement = z.infer<typeof CareerSkillRequirementSchema>;

export interface ReferentialWarning {
  level: "error" | "warn";
  message: string;
}

export function collectReferentialIssues(catalog: Catalog): ReferentialWarning[] {
  const warnings: ReferentialWarning[] = [];
  const sourceIds = new Set(catalog.sources.map((s) => s.id));
  const skillIds = new Set(catalog.skills.map((s) => s.id));
  const careerIds = new Set(catalog.careers.map((c) => c.id));
  const techIds = new Set(catalog.technologies.map((t) => t.id));
  const sectorIds = new Set(catalog.sectors.map((s) => s.id));
  const resourceIds = new Set(catalog.resources.map((r) => r.id));
  const domainIds = new Set(catalog.domains.map((d) => d.id));
  const regionIds = new Set(catalog.regions.map((r) => r.id));

  const dup = (kind: string, ids: string[]) => {
    const seen = new Set<string>();
    for (const id of ids) {
      if (seen.has(id)) warnings.push({ level: "error", message: `Duplicate ${kind} id: ${id}` });
      seen.add(id);
    }
  };
  dup("skill", catalog.skills.map((s) => s.id));
  dup("career", catalog.careers.map((c) => c.id));
  dup("technology", catalog.technologies.map((t) => t.id));
  dup("trend", catalog.trends.map((t) => t.id));
  dup("source", catalog.sources.map((s) => s.id));
  dup("resource", catalog.resources.map((r) => r.id));
  dup("sector", catalog.sectors.map((s) => s.id));

  const checkIds = (kind: string, owner: string, ids: string[], set: Set<string>) => {
    for (const id of ids) {
      if (!set.has(id)) warnings.push({ level: "error", message: `${kind} ${owner} references unknown id ${id}` });
    }
  };

  const checkGeo = (kind: string, id: string, geo: string[]) => {
    for (const g of geo) {
      if (!regionIds.has(g)) warnings.push({ level: "error", message: `${kind} ${id} has unknown geography ${g}` });
    }
  };

  for (const skill of catalog.skills) {
    checkIds("skill", skill.id, skill.sourceIds, sourceIds);
    checkIds("skill", skill.id, skill.relatedCareerIds, careerIds);
    checkIds("skill", skill.id, skill.relatedTechnologyIds, techIds);
    checkIds("skill", skill.id, skill.relatedResourceIds, resourceIds);
    checkIds("skill", skill.id, skill.domainIds, domainIds);
    checkGeo("skill", skill.id, skill.geography);
  }
  for (const tech of catalog.technologies) {
    checkIds("technology", tech.id, tech.sourceIds, sourceIds);
    checkIds("technology", tech.id, tech.relatedSkillIds, skillIds);
    checkIds("technology", tech.id, tech.relatedCareerIds, careerIds);
    checkIds("technology", tech.id, tech.relatedSectorIds, sectorIds);
    checkGeo("technology", tech.id, tech.geography);
  }
  for (const career of catalog.careers) {
    checkIds("career", career.id, career.sourceIds, sourceIds);
    checkIds("career", career.id, career.relatedCareerIds, careerIds);
    checkIds("career", career.id, career.relatedTechnologyIds, techIds);
    checkIds("career", career.id, career.sectorIds, sectorIds);
    checkGeo("career", career.id, career.geography);
    if (career.requiredSkills.length < 8) {
      warnings.push({ level: "error", message: `Career ${career.id} has no complete skill requirements` });
    }
    for (const req of career.requiredSkills) {
      if (!skillIds.has(req.skillId)) {
        warnings.push({ level: "error", message: `Career ${career.id} requires unknown skill ${req.skillId}` });
      }
      checkIds("career-requirement", career.id, req.sourceIds, sourceIds);
    }
  }
  for (const trend of catalog.trends) {
    checkIds("trend", trend.id, trend.sourceIds, sourceIds);
    checkIds("trend", trend.id, trend.relatedSkillIds, skillIds);
    checkIds("trend", trend.id, trend.relatedCareerIds, careerIds);
    checkIds("trend", trend.id, trend.relatedTechnologyIds, techIds);
    checkGeo("trend", trend.id, trend.geography);
  }
  for (const cluster of catalog.clusters) {
    checkIds("cluster", cluster.id, cluster.exampleCareerIds, careerIds);
  }
  for (const q of catalog.questions) {
    if (!skillIds.has(q.skillId)) warnings.push({ level: "error", message: `Question ${q.id} unknown skill` });
  }
  for (const res of catalog.resources) {
    checkIds("resource", res.id, res.sourceIds, sourceIds);
    checkIds("resource", res.id, res.skillsDeveloped, skillIds);
    if (res.availability === "available" && !res.url) {
      warnings.push({ level: "error", message: `Resource ${res.id} is available but has no URL` });
    }
  }
  return warnings;
}
